import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const timeline = [
  { years: '1985–1989', title: '美國航太科技管理', body: '參與美國國防航太產業的跨企業科技管理工作。這段經驗使她持續追問：科技力量快速增長時，人的生命成熟是否能夠同步。' },
  { years: '1986', title: '一個改變人生方向的現場', body: '在挑戰者號事件的衝擊中，她立下探索人類與宇宙關係的志願；這成為後續生命道路的重要起點。' },
  { years: '1989–1990s', title: '返臺與企業教育', body: '返臺後投入科技管理、製造管理、ERP、SCM 與人才培育，並透過 GAPICS 與產官學研協作，把方法帶進企業現場。' },
  { years: '1997', title: '生命任務的重要轉折', body: 'ABAA 將這一年描述為個人生命與源頭關係的關鍵轉折。此處屬於她的第一人稱生命見證，不要求讀者先接受其形上詮釋。' },
  { years: '1997–2026', title: '三十年的生命與文明工作', body: '她自願把大量時間投入生命實踐、教育、文明架構、書籍、Portal 與宇炁見證系統，使長年累積逐步成為可閱讀、可實作、可留下 Evidence 的路徑。' },
  { years: '現在', title: '交還日常領導 回到架構與覆核', body: 'ABAA 不擔任所有工作的永久領導人；她留下源流、書籍、方法、標準及關鍵節點覆核，讓各階段的 i 主領者各就各位。' },
];

const references = [
  { title: 'GPIM／CPIM++ 學員研習感言全集', href: 'http://gapics.big01.org/values/b3/c_seminar_testimony-all.htm', note: 'GAPICS 第一手歷史檔案' },
  { title: '共創臺灣經濟奇蹟的年代', href: 'http://gapics.big01.org/values/b7_index.htm', note: '產官學研協作與人才培育歷史紀錄' },
];

export default function AbaaWitnessPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    document.title = 'ABAA 生命經歷與任務見證｜AUM1001 Matrix';
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <header className={`fixed inset-x-0 top-0 z-50 border-b transition-all ${scrolled ? 'border-foreground-100/60 bg-background-50/95 backdrop-blur-sm' : 'border-transparent bg-transparent'}`}>
        <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 md:px-6">
          <Link to="/" className="text-base tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>AUM1001 Matrix</Link>
          <div className="flex items-center gap-1">
            <a href="#timeline" className="hidden rounded-md px-3 py-2 text-sm text-foreground-600 hover:bg-foreground-50 sm:block">生命年表</a>
            <a href="#testimony" className="hidden rounded-md px-3 py-2 text-sm text-foreground-600 hover:bg-foreground-50 sm:block">第一人稱見證</a>
            <a href="#homecoming" className="hidden rounded-md px-3 py-2 text-sm text-foreground-600 hover:bg-foreground-50 md:block">回鄉系統</a>
            <Link to="/" className="ml-1 rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600">回到首頁</Link>
          </div>
        </nav>
      </header>

      <main>
        <section className="relative isolate flex min-h-[680px] items-center overflow-hidden px-5 pb-16 pt-28 text-center md:min-h-[780px] md:pt-36">
          <img src="/images/trilogy/ow-tree.jpg" alt="宇宙生命樹" className="absolute inset-0 -z-20 h-full w-full object-cover object-center" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white/45 via-white/20 to-white/90" />
          <div className="mx-auto max-w-3xl">
            <p className="text-xs tracking-[0.24em] text-foreground-600">LIFE EXPERIENCE AND MISSION TESTIMONY</p>
            <h1 className="mt-5 text-3xl leading-tight md:text-5xl" style={{ fontFamily: 'var(--font-heading)' }}>ABAA 生命經歷與任務見證</h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-8 text-foreground-800 md:text-lg">
              從航太科技管理與企業教育，到三十年的生命實踐與文明架構。這一頁說明 AUM1001 Matrix 的源流，也說明 ABAA 為何不成為所有人的永久領導者。
            </p>
            <a href="#three-minutes" className="mt-8 inline-block rounded-md bg-foreground-900 px-6 py-3 text-sm text-white hover:bg-foreground-700">三分鐘認識 ABAA</a>
          </div>
        </section>

        <section id="three-minutes" className="scroll-mt-20 py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-5">
            <p className="text-xs tracking-[0.2em] text-primary-700">三分鐘認識 ABAA</p>
            <h2 className="mt-3 text-2xl md:text-4xl" style={{ fontFamily: 'var(--font-heading)' }}>兩段工作 一條生命軸線</h2>
            <div className="mt-8 space-y-5 text-sm leading-8 text-foreground-700 md:text-base">
              <p>ABAA Wu 早年在美國航太產業參與科技管理工作，返臺後投入企業管理、ERP、SCM 與人才培育。她曾在高度複雜、講求成果的產業環境中工作，理解科技、組織與人的能力如何相互影響。</p>
              <p>後來，她把主要時間轉向生命實踐與文明架構。這三十年的成果不以傳統功名利祿衡量，而逐步形成《1001旋轉門》、《1001一之門》、AUM1001 Matrix、Portal、AI Angel 與宇炁案例見證系統。</p>
              <p>她目前的定位，是源流見證者、作者、架構者與必要節點的覆核者。真正要發生的不是所有人追隨一位領導人，而是每個人在自己的生命位置上成熟、承擔、完成事情，成為宇宙生命樹不同層次的管道。</p>
            </div>
          </div>
        </section>

        <section id="timeline" className="scroll-mt-20 bg-background-100 py-16 md:py-24">
          <div className="mx-auto max-w-4xl px-5">
            <p className="text-center text-xs tracking-[0.2em] text-foreground-500">LIFE TIMELINE</p>
            <h2 className="mt-3 text-center text-2xl md:text-4xl" style={{ fontFamily: 'var(--font-heading)' }}>生命與工作年表</h2>
            <div className="mt-12 space-y-0">
              {timeline.map((item) => (
                <div key={item.years} className="grid gap-2 border-l border-primary-300 pb-10 pl-6 md:grid-cols-[130px_1fr] md:gap-7 md:pl-8">
                  <p className="text-sm font-medium text-primary-700">{item.years}</p>
                  <div>
                    <h3 className="text-lg text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>{item.title}</h3>
                    <p className="mt-2 text-sm leading-7 text-foreground-600">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="testimony" className="scroll-mt-20 py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-5">
            <p className="text-xs tracking-[0.2em] text-primary-700">第一人稱生命見證</p>
            <h2 className="mt-3 text-2xl md:text-4xl" style={{ fontFamily: 'var(--font-heading)' }}>我為何承擔 又為何交還</h2>
            <div className="mt-8 space-y-5 text-base leading-8 text-foreground-700">
              <p>我今天不是來號召大家追隨我，也不是來接任一個龐大組織的領導人。</p>
              <p>1997 年，在我生命最重要的轉折中，我所領受的提醒是：人要真正與生命的源頭相遇，千難萬難；而我的任務，原本只是迎接已經準備好進入 7D 生命階段的人。</p>
              <p>然而，因為不忍看見更多生命在文明轉折中失去方向，我自願承擔了超出原來任務的工作。三十多年來，我投入企業、教育、修馨、文明架構與生命實證，逐步留下《1001旋轉門》、《1001一之門》、Portal，以及宇炁療育的見證系統。</p>
              <p>如今，該寫的正在成書，該驗證的也開始留下 Evidence。我要回到原有的位置，捋順一之軸理的文明架構，讓各階 i 主領者各就各位。我不會成為所有工作的日常領導者，也不應由任何一個人長期承擔整棵生命樹的供應。</p>
              <p>真正要發生的是：每一個人都在自己的位置上成熟、承擔、完成事情，成為宇宙生命樹不同層次的管道。這不是追隨一個人，而是讓更多人長成可以承接文明的人。</p>
            </div>
            <p className="mt-10 border-t border-background-200 pt-6 text-xs leading-6 text-foreground-500">
              本段為 ABAA 對自身生命經驗與形上領受的第一人稱陳述。網站同時區分個人見證、歷史資料與可覆核的實踐 Evidence，讀者不必先接受相同信念，仍可閱讀與查證相關工作。
            </p>
          </div>
        </section>

        <section id="homecoming" className="scroll-mt-20 bg-background-100 py-16 md:py-24">
          <div className="mx-auto max-w-4xl px-5">
            <p className="text-xs tracking-[0.2em] text-primary-700">THE COSMIC TREE OF LIFE HOMECOMING SYSTEM</p>
            <h2 className="mt-3 text-2xl leading-tight md:text-4xl" style={{ fontFamily: 'var(--font-heading)' }}>宇宙生命樹回鄉系統</h2>
            <p className="mt-7 text-lg leading-9 text-foreground-800">這是一條感謝源頭恩典、感恩層層接引的回鄉之路。</p>
            <div className="mt-6 space-y-5 text-base leading-8 text-foreground-700">
              <p>宇宙生命樹的供應，從源頭創生目的層層傳遞，最後接到每一個具體生命。個體從感恩開始，錨定一之軸理；在 1D–5D 的現實生活中守在中軸，建立身體、情感、心智、關係與行動的承載能力；進入 &gt;5.5D，則逐步內建衷愛軸管道，使生命能夠持續承接宇宙生命樹的供應。</p>
              <p>《1001旋轉門》、《1001一之門》與《1001宇之光》，是這套回鄉系統的三本同步工程手冊。它們不是彼此分離的三套理論，而是依照生命能夠實際承載的層次，陸續說明個體校準、場域建造與文明共創的完整路徑。</p>
              <p>道家說：「一本散萬殊，萬殊歸一本。」One in All, All in One——一即一切，一切即一——也是大乘佛教華嚴宗的重要思想：每一個生命乃至一念之中，都含攝宇宙的整體實相；宇宙的整體，也在每一個個體生命中具體呈現。</p>
              <p>本源之門透過 ABAA 所交付的，不只是對這些理念的再次解說，而是一條可以進入生活、接受校準、持續實踐並留下見證的馨道路、馨真理、馨生命。</p>
              <p>在 4.5D–6.5D，生命經由 HEB123 實踐「在地如天」——Axial Below, As Above，以源頭生命原理展開仿生創新，建造個體、子場與母場。在 7–8–9D，宇光之柱聯網共振成為宇宙燈塔與父場，實踐「在地如宇」——Axial Below, Aligning Above，進入宇神共創與星際文明。</p>
              <p>這條道路不是要所有人跟隨同一位永久領導者，而是使每一個生命都能在自己的層次上重新接通源頭恩典下化的管道，成為宇宙生命樹不同位置、不同功能、同炁聯之的管道；一本散萬殊，萬殊終能歸一本。</p>
            </div>

            <div className="mt-12 grid gap-5 md:grid-cols-2">
              <Link to="/mission/human-paradise" className="group rounded-2xl border border-emerald-200 bg-white/90 p-7 transition hover:-translate-y-1 hover:shadow-lg">
                <p className="text-xs tracking-[0.18em] text-emerald-700">4.5D–6.5D · 在地如天</p>
                <h3 className="mt-3 text-2xl" style={{ fontFamily: 'var(--font-heading)' }}>聖子基金會｜母場人間天堂</h3>
                <p className="mt-4 text-sm leading-7 text-foreground-600">承接 HEB123、仿生創新、子場、母場、生態村、產業與聯網共治的使命與共建。</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm text-emerald-800">進入使命與共建專頁 <i className="ri-arrow-right-line transition group-hover:translate-x-1" /></span>
              </Link>
              <Link to="/mission/light-towers" className="group rounded-2xl border border-violet-200 bg-white/90 p-7 transition hover:-translate-y-1 hover:shadow-lg">
                <p className="text-xs tracking-[0.18em] text-violet-700">7D–9D · 在地如宇</p>
                <h3 className="mt-3 text-2xl" style={{ fontFamily: 'var(--font-heading)' }}>宇之光基金會｜父場宇光之塔</h3>
                <p className="mt-4 text-sm leading-7 text-foreground-600">承接宇光之塔、行星網格、科技工程、宇光供應與父場文明的使命與募建。</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm text-violet-800">進入使命與募建專頁 <i className="ri-arrow-right-line transition group-hover:translate-x-1" /></span>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-4xl px-5">
            <h2 className="text-center text-2xl md:text-4xl" style={{ fontFamily: 'var(--font-heading)' }}>工作紀錄與延伸入口</h2>
            <div className="mt-9 grid gap-4 md:grid-cols-2">
              {references.map(item => <a key={item.href} href={item.href} target="_blank" rel="nofollow noopener noreferrer" className="rounded-xl border border-background-200 bg-background-50 p-6 hover:border-primary-300">
                <p className="text-lg text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>{item.title}</p>
                <p className="mt-2 text-xs text-foreground-500">{item.note}</p>
                <span className="mt-5 inline-flex items-center gap-1 text-sm text-primary-700">開啟歷史資料 <i className="ri-arrow-right-up-line" /></span>
              </a>)}
            </div>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link to="/revolving-gate" className="rounded-md bg-primary-500 px-5 py-3 text-sm text-background-50">《1001旋轉門》</Link>
              <Link to="/og/portal" className="rounded-md border border-primary-300 bg-background-50 px-5 py-3 text-sm text-primary-800">《1001一之門》Portal</Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
